import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Download, ExternalLink, FileText, Maximize2 } from 'lucide-react';
import Seo from '../components/Seo';
import Markdown, { extractHeadings } from '../components/resources/Markdown';
import ResourceCard from '../components/resources/ResourceCard';
import {
  RESOURCE_PRODUCTS, RESOURCE_TYPES, assetUrl, formatDate, formatSize, metaParts, typeInfo, videoEmbed,
} from '../lib/resources';
import { faqPageLd, jsonLd } from '../lib/jsonLd';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const SITE = 'https://voip2.co.uk';

// Questions and answers under the article (plain text, rendered as React text nodes).
const Faqs = ({ items }) => (
  <section aria-labelledby="faq-heading" className="mt-14 border-t border-gray-100 pt-10">
    <h2 id="faq-heading" className="font-display text-2xl font-semibold text-[#0a1f2e]">Frequently asked questions</h2>
    <div className="mt-6 divide-y divide-gray-100 rounded-2xl border border-gray-100">
      {items.map((f, i) => (
        <details key={i} className="group px-5 py-4" open={i === 0}>
          <summary className="flex cursor-pointer list-none items-start justify-between gap-4 font-semibold text-[#0a1f2e]">
            <span>{f.question}</span>
            <span aria-hidden="true" className="mt-0.5 text-gray-400 transition-transform group-open:rotate-45">+</span>
          </summary>
          <p className="mt-3 leading-relaxed text-gray-700">{f.answer}</p>
        </details>
      ))}
    </div>
  </section>
);

const btnPrimary = 'inline-flex items-center justify-center gap-2 rounded-full bg-[#c8ff00] px-6 py-3 text-sm font-semibold text-[#0a1f2e] transition hover:brightness-95 active:scale-[0.98]';
const btnGhost = 'inline-flex items-center justify-center gap-2 rounded-full border border-[#0a1f2e]/20 px-6 py-3 text-sm font-semibold text-[#0a1f2e] transition-colors hover:border-[#0a1f2e]';

const VideoPlayer = ({ r }) => {
  const fileVideo = r.file && (r.file.content_type || '').startsWith('video/') ? { kind: 'video', src: assetUrl(r.file.url) } : null;
  const embed = fileVideo || videoEmbed(r.video_url ? assetUrl(r.video_url) : '');
  if (!embed) return null;
  return (
    <div className="mb-10 aspect-video overflow-hidden rounded-2xl bg-[#0a1f2e]">
      {embed.kind === 'iframe' ? (
        <iframe
          src={embed.src}
          title={r.title}
          className="h-full w-full"
          loading="lazy"
          allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      ) : (
        <video src={embed.src} controls preload="metadata" className="h-full w-full" poster={r.cover_image ? assetUrl(r.cover_image) : undefined} />
      )}
    </div>
  );
};

const FilePreview = ({ r }) => {
  const f = r.file;
  const url = assetUrl(f.url);
  const ext = (f.ext || '').toLowerCase();
  if (['png', 'jpg', 'webp'].includes(ext)) {
    return <img src={url} alt={r.title} className="mb-10 w-full rounded-2xl border border-gray-100" />;
  }
  if (ext !== 'pdf') return null;
  return (
    <div className="mb-10">
      {/* Phones do not show PDFs inside a page, so they get a card that opens the file instead. */}
      <div className="hidden overflow-hidden rounded-2xl border border-gray-200 bg-[#f5f7f9] md:block">
        <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-2.5 text-sm">
          <span className="flex items-center gap-2 font-medium text-gray-600"><FileText className="h-4 w-4" /> Preview</span>
          <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-medium text-[#0a1f2e] hover:underline">
            <Maximize2 className="h-4 w-4" /> Open full screen
          </a>
        </div>
        <iframe src={`${url}#view=FitH&navpanes=0`} title={`${r.title} (PDF preview)`} className="h-[78vh] w-full bg-white" loading="lazy" />
      </div>
      <a href={url} target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-4 rounded-2xl border border-gray-200 p-4 md:hidden">
        <span className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-[#0a1f2e] text-[#c8ff00]"><FileText className="h-6 w-6" /></span>
        <span className="min-w-0">
          <span className="block truncate font-semibold text-[#0a1f2e]">{f.name || r.title}</span>
          <span className="block text-sm text-gray-500">{[metaParts(r)[0], formatSize(f.size)].filter(Boolean).join(' · ')} · Tap to open</span>
        </span>
      </a>
    </div>
  );
};

const Toc = ({ headings }) => (
  <nav aria-label="On this page" className="rounded-2xl border border-gray-100 bg-[#f5f7f9] p-5">
    <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">On this page</p>
    <ul className="space-y-2 text-sm">
      {headings.map((h) => (
        <li key={h.id} className={h.level === 3 ? 'pl-3' : ''}>
          <a href={`#${h.id}`} className="text-gray-600 transition-colors hover:text-[#0a1f2e]">{h.text}</a>
        </li>
      ))}
    </ul>
  </nav>
);

const ResourceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    const ctrl = new AbortController();
    setState('loading');
    fetch(`${BACKEND_URL}/api/resources/slug/${encodeURIComponent(slug)}`, { signal: ctrl.signal })
      .then((res) => {
        if (res.status === 404) { setState('missing'); return null; }
        if (!res.ok) throw new Error('load');
        return res.json();
      })
      .then((d) => {
        if (!d) return;
        if (d.slug !== slug) { navigate(`/resources/${d.slug}`, { replace: true }); return; }
        setR(d);
        setState('ready');
      })
      .catch((e) => { if (e.name !== 'AbortError') setState('error'); });
    return () => ctrl.abort();
  }, [slug, navigate]);

  const headings = useMemo(() => extractHeadings(r?.body), [r]);

  if (state === 'loading') {
    return (
      <div className="min-h-screen px-4 pb-24 pt-32 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl animate-pulse">
          <div className="h-3 w-24 rounded bg-gray-100" />
          <div className="mt-6 h-10 w-3/4 rounded bg-gray-100" />
          <div className="mt-4 h-4 w-1/2 rounded bg-gray-100" />
          <div className="mt-10 h-72 rounded-2xl bg-gray-100" />
        </div>
      </div>
    );
  }

  if (state !== 'ready') {
    return (
      <div className="min-h-screen px-4 pb-24 pt-32 text-center sm:px-6 lg:px-8">
        <Helmet><title>{state === 'missing' ? 'Resource not found' : 'Resources'} | VOIP2</title><meta name="robots" content="noindex" /></Helmet>
        <h1 className="font-display text-3xl font-semibold text-[#0a1f2e]">{state === 'missing' ? 'Resource not found' : 'This page could not be loaded'}</h1>
        <p className="mt-3 text-gray-600">{state === 'missing' ? 'It may have moved or been retired.' : 'Please try again in a moment.'}</p>
        <Link to="/resources" className={`${btnPrimary} mt-8`}><ArrowLeft className="h-4 w-4" /> All resources</Link>
      </div>
    );
  }

  const { label, plural, icon: Icon } = { ...typeInfo(r.type), plural: RESOURCE_TYPES[r.type]?.plural };
  const isVideo = r.type === 'video' || r.type === 'webinar';
  const fileUrl = r.file ? assetUrl(r.file.url) : '';
  const isPdf = (r.file?.ext || '').toLowerCase() === 'pdf';
  const showToc = headings.length >= 3;
  const meta = [...metaParts(r), r.file?.size && !isVideo ? formatSize(r.file.size) : null, r.updated_at ? `Updated ${formatDate(r.updated_at)}` : null]
    .filter(Boolean);
  const canonical = `${SITE}/resources/${r.slug}`;
  const description = r.meta_description || r.summary || `${label} from VOIP2.`;
  const ld = {
    '@context': 'https://schema.org',
    '@type': isVideo ? 'VideoObject' : (r.type === 'how_to' ? 'HowTo' : r.type === 'article' ? 'Article' : 'CreativeWork'),
    name: r.title,
    headline: r.title,
    description,
    dateModified: r.updated_at,
    ...(isVideo ? { uploadDate: r.published_at || r.updated_at, thumbnailUrl: r.cover_image ? assetUrl(r.cover_image) : undefined } : {}),
    publisher: { '@type': 'Organization', name: 'VOIP2', url: SITE },
    mainEntityOfPage: canonical,
    ...(r.published_at ? { datePublished: r.published_at } : {}),
  };
  // Social cards need an absolute address: the cover (an upload on the API, or a /media file) or the site default.
  const coverAbs = r.cover_image ? assetUrl(r.cover_image) : '';
  const ogImage = coverAbs ? (coverAbs.startsWith('/') ? `${SITE}${coverAbs}` : coverAbs) : undefined;
  if (ogImage && !isVideo) ld.image = ogImage;
  const faqs = (r.faqs || []).filter((f) => f.question && f.answer);
  const faqLd = faqPageLd(faqs, canonical);

  return (
    <div className="min-h-screen bg-white text-[#0a1f2e]">
      <Seo title={r.meta_title || r.title} description={description} image={ogImage} type="article" />
      <Helmet>
        <link rel="canonical" href={canonical} />
        <script type="application/ld+json">{jsonLd(ld)}</script>
        {faqLd && <script type="application/ld+json">{jsonLd(faqLd)}</script>}
      </Helmet>

      <header className="px-4 pb-10 pt-28 sm:px-6 lg:px-8 lg:pt-32">
        <div className="mx-auto max-w-6xl">
          <nav aria-label="Breadcrumb" className="mb-8 flex items-center gap-2 text-sm text-gray-500">
            <Link to="/resources" className="hover:text-[#0a1f2e]">Resources</Link>
            <span aria-hidden="true">/</span>
            <Link to={`/resources?type=${r.type}`} className="hover:text-[#0a1f2e]">{plural || label}</Link>
          </nav>
          <div className="max-w-3xl">
            <div className="mb-5 flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#0a1f2e] text-[#c8ff00]"><Icon className="h-[18px] w-[18px]" /></span>
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</span>
            </div>
            <h1 className="font-display text-4xl font-semibold leading-[1.1] tracking-tight md:text-5xl">{r.title}</h1>
            {r.summary && <p className="mt-5 text-lg leading-relaxed text-gray-600">{r.summary}</p>}
            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500">
              <span>{meta.join(' · ')}</span>
              {(r.products || []).length > 0 && (
                <span className="flex flex-wrap gap-1.5">
                  {r.products.map((p) => (
                    <Link key={p} to={`/resources?product=${p}`} className="rounded-full bg-[#f5f7f9] px-2.5 py-0.5 text-xs font-medium text-gray-600 hover:text-[#0a1f2e]">
                      {RESOURCE_PRODUCTS[p] || p}
                    </Link>
                  ))}
                </span>
              )}
            </div>
            {(r.file && !isVideo) || r.external_url ? (
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                {r.file && !isVideo && (
                  <a href={fileUrl} target="_blank" rel="noopener noreferrer" download={isPdf ? undefined : (r.file.name || true)} className={btnPrimary}>
                    <Download className="h-4 w-4" /> Download {(r.file.ext || 'file').toUpperCase()}
                  </a>
                )}
                {r.external_url && (
                  <a href={r.external_url} target="_blank" rel="noopener noreferrer" className={r.file ? btnGhost : btnPrimary}>
                    <ExternalLink className="h-4 w-4" /> {r.type === 'webinar' ? 'Register' : 'Open link'}
                  </a>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <main className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className={`mx-auto max-w-6xl ${showToc ? 'lg:grid lg:grid-cols-12 lg:gap-12' : ''}`}>
          <div className={showToc ? 'lg:col-span-8' : 'max-w-3xl'}>
            {isVideo && <VideoPlayer r={r} />}
            {r.file && !isVideo && <FilePreview r={r} />}
            {r.cover_image && !r.file && !isVideo && (
              <img src={assetUrl(r.cover_image)} alt="" className="mb-10 aspect-[16/9] w-full rounded-2xl object-cover" />
            )}
            {showToc && <div className="mb-8 lg:hidden"><Toc headings={headings} /></div>}
            {r.body && <Markdown source={r.body} />}
            {faqs.length > 0 && <Faqs items={faqs} />}
          </div>
          {showToc && (
            <aside className="hidden lg:col-span-4 lg:block">
              <div className="sticky top-28"><Toc headings={headings} /></div>
            </aside>
          )}
        </div>
      </main>

      {r.related?.length > 0 && (
        <section className="border-t border-gray-100 bg-[#f5f7f9] px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="mb-6 flex items-baseline justify-between gap-4">
              <h2 className="font-display text-2xl font-semibold">Related resources</h2>
              <Link to="/resources" className="text-sm font-medium text-gray-500 hover:text-[#0a1f2e]">All resources</Link>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {r.related.map((x) => <ResourceCard key={x.id} r={x} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ResourceDetail;
